/**
 * Untamed v2.1 - Product Page JavaScript
 * ============================================
 * Handles variant selection, option availability, and media gallery interactions.
 */

(function() {
  'use strict';

  /**
   * Product Variant Selector Class
   * Handles variant selection, option availability updates, and UI synchronization
   */
  class ProductVariantSelector {
    constructor(config) {
      this.variants = config.variants || [];
      this.productForm = window.HealixProductForm;
      this.options = this.productForm
        ? this.productForm.normalizeOptions(config.options || [], this.variants)
        : (config.options || []);
      this.moneyFormat = config.moneyFormat || window.themeMoneyFormat || '{{amount}}';
      this.currentVariantId = config.currentVariantId;
      this.sectionId = config.sectionId || null;
      // Option names (lowercase) that must start UNSELECTED (no default variant
      // pre-picked). Empty when the page was opened with a ?variant= deep link.
      this.deferredOptions = (config.deferredOptions || []).map(n => String(n).toLowerCase());
      
      // Get container (scoped to section if sectionId provided)
      this.container = this.sectionId 
        ? document.querySelector(`[data-section-id="${this.sectionId}"]`)
        : document;
      
      if (!this.container) return;
      
      // Initialize selected options from current variant.
      // Deferred options (size/length) start as null = unselected.
      this.selectedOptions = {};
      const currentVariant = this.variants.find(v => v.id === this.currentVariantId) ||
        (this.productForm && this.productForm.firstAvailableVariant(this.variants)) ||
        this.variants[0] || null;
      this.flashDeal = null;
      this.selectedOptions = this.productForm
        ? this.productForm.initialSelection({
          options: this.options,
          variants: this.variants,
          currentVariant: currentVariant,
          deferredOptions: this.deferredOptions
        })
        : this.options.reduce((selection, option, index) => {
          const optionKey = 'option' + (index + 1);
          selection[option.name] = this.deferredOptions.includes(String(option.name).toLowerCase())
            ? null
            : (currentVariant ? currentVariant[optionKey] : option.values[0]);
          return selection;
        }, {});
      this.currentVariant = currentVariant;
      
      this.init();
      this.initFlashDeal();
    }
    
    init() {
      this.syncUIState();
      this.updateOptionAvailability();
      this.bindEvents();
      this.initPendingGate();
    }
    
    syncUIState() {
      // Sync button active states to match current variant
      Object.entries(this.selectedOptions).forEach(([optionName, optionValue]) => {
        if (optionValue == null) return; // deferred option: nothing to mark active
        const btn = this.container.querySelector(`[data-option-name="${optionName}"][data-option-value="${optionValue}"]`);
        if (btn) {
          const container = btn.closest('.product-variants-block__pills, .product-variants-block__swatches, .product-variants-block__rich-media');
          if (container) {
            container.querySelectorAll('button').forEach(b => b.classList.remove('active'));
          }
          btn.classList.add('active');
          
          const label = btn.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
          if (label) label.textContent = ' ' + optionValue;
        }
      });
      
      // Deferred (unselected) options: strip any server-rendered active state
      // so no size/length chip looks pre-picked (belt & braces with the Liquid).
      this.options.forEach((option, index) => {
        if (this.selectedOptions[option.name] != null) return;
        const group = this.optionGroup(option, index);
        if (group) {
          group.querySelectorAll('button.active').forEach(b => b.classList.remove('active'));
        }
      });
    }
    
    optionGroup(option, index) {
      const pos = option.position || (index + 1);
      return this.container.querySelector(`.product-variants-block__option[data-option-position="${pos}"]`);
    }
    
    bindEvents() {
      // Pills and swatches
      this.container.querySelectorAll('.product-variants-block__pill, .product-variants-block__swatch, .product-variants-block__rich-media-item').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleOptionClick(e));
      });
      
      // Dropdowns
      this.container.querySelectorAll('.product-variants-block__dropdown').forEach(dropdown => {
        dropdown.addEventListener('select:change', (e) => this.handleDropdownChange(e, dropdown));
      });
    }
    
    handleOptionClick(e) {
      const btn = e.currentTarget;
      if (btn.disabled) return;
      
      const optionName = btn.dataset.optionName;
      const optionValue = btn.dataset.optionValue;
      
      // Update active state
      const container = btn.closest('.product-variants-block__pills, .product-variants-block__swatches, .product-variants-block__rich-media');
      if (container) {
        container.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      }
      btn.classList.add('active');
      
      // Update label
      const label = btn.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
      if (label) label.textContent = ' ' + optionValue;
      
      // The user picked this group: clear its "please select" prompt if shown
      const optGroup = btn.closest('.product-variants-block__option');
      if (optGroup) this.clearPrompt(optGroup);
      
      // Update selection and find variant
      this.selectedOptions[optionName] = optionValue;
      this.updateOptionAvailability();
      this.updatePendingState();
      
      const variant = this.findVariant();
      if (variant) {
        this.updateVariant(variant);
      }
    }
    
    handleDropdownChange(e, dropdown) {
      const optionName = dropdown.dataset.optionName;
      const optionValue = e.detail.value;
      
      const label = dropdown.closest('.product-variants-block__option')?.querySelector('.product-variants-block__selected-value');
      if (label) label.textContent = ' ' + optionValue;
      
      const optGroup = dropdown.closest('.product-variants-block__option');
      if (optGroup) this.clearPrompt(optGroup);
      
      this.selectedOptions[optionName] = optionValue;
      this.updateOptionAvailability();
      this.updatePendingState();
      
      const variant = this.findVariant();
      if (variant) {
        this.updateVariant(variant);
      }
    }
    
    /* ------------------------------------------------------------------
     * Pending gate — "no default size/length" behaviour.
     * While any deferred option is unselected the buy buttons stay CLICKABLE
     * but read "Select a size" / "Select a length" / "Select options";
     * clicking them scrolls to the first unselected option group and shows a
     * red prompt instead of adding to cart. Once every option is picked the
     * normal variant flow takes over (updateVariant + variant:changed).
     * ---------------------------------------------------------------- */
    
    missingOptions() {
      return this.options.filter(o => this.selectedOptions[o.name] == null);
    }
    
    pendingLabel() {
      const missing = this.missingOptions();
      if (!missing.length) return null;
      if (missing.length > 1) return 'Select options';
      return 'Select a ' + String(missing[0].name).toLowerCase();
    }
    
    updatePendingState() {
      if (!this.deferredOptions.length) return;
      const label = this.pendingLabel();
      const pending = label != null;
      
      // Main add-to-cart button(s): pending look, but NOT disabled (clickable prompt)
      this.container.querySelectorAll('.product-add-to-cart-block__btn').forEach(btn => {
        btn.classList.toggle('is-pending', pending);
        if (pending) {
          btn.disabled = false;
          const t = btn.querySelector('[data-add-to-cart-text]');
          if (t) t.textContent = label;
        }
      });
      
      // Dynamic checkout (Buy it now / wallets): dim + click-shield while pending
      this.container.querySelectorAll('.product-add-to-cart-block__dynamic-checkout').forEach(c => {
        c.classList.toggle('is-pending', pending);
      });
      
      // Floating/sticky bar buttons
      document.querySelectorAll('[data-floating-btn="add_to_cart"], [data-floating-btn="buy_now"]').forEach(btn => {
        btn.classList.toggle('is-pending', pending);
        if (pending) {
          btn.disabled = false;
          const t = btn.querySelector('[data-btn-text]');
          if (t) t.textContent = label;
        }
      });
      
      if (!pending) {
        this.container.querySelectorAll('.product-variants-block__option.is-prompt').forEach(g => this.clearPrompt(g));
      }
    }
    
    initPendingGate() {
      if (!this.deferredOptions.length) return;
      this.updatePendingState();
      
      // Capture-phase interceptors run before the block/floating-bar handlers.
      document.addEventListener('submit', (e) => {
        if (!this.pendingLabel()) return;
        if (!e.target.closest || !e.target.closest('.product-add-to-cart-block__form')) return;
        e.preventDefault();
        e.stopPropagation();
        this.promptMissing();
      }, true);
      
      document.addEventListener('click', (e) => {
        if (!this.pendingLabel()) return;
        if (!e.target.closest) return;
        const gated = e.target.closest('[data-floating-btn="add_to_cart"], [data-floating-btn="buy_now"], .product-add-to-cart-block__dynamic-checkout');
        if (!gated) return;
        e.preventDefault();
        e.stopPropagation();
        this.promptMissing();
      }, true);
    }
    
    promptMissing() {
      const missing = this.missingOptions();
      if (!missing.length) return;
      const first = missing[0];
      const idx = this.options.indexOf(first);
      const group = this.optionGroup(first, idx);
      if (!group) return;
      
      let msg = group.querySelector('.product-variants-block__prompt-msg');
      if (!msg) {
        msg = group.querySelector('[data-option-label-text]');
        if (!msg) return;
        msg.setAttribute('data-original-label', msg.textContent.trim());
        msg.className = 'product-variants-block__prompt-msg';
      }
      msg.textContent = 'Please select a ' + String(first.name).toLowerCase();
      
      // Restart the pulse animation on repeat clicks
      group.classList.remove('is-prompt');
      void group.offsetWidth;
      group.classList.add('is-prompt');
      
      const rect = group.getBoundingClientRect();
      if (rect.top < 80 || rect.bottom > window.innerHeight - 80) {
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    clearPrompt(group) {
      group.classList.remove('is-prompt');
      const msg = group.querySelector('.product-variants-block__prompt-msg');
      if (msg) {
        msg.textContent = msg.getAttribute('data-original-label') || '';
        msg.removeAttribute('data-original-label');
        msg.classList.remove('product-variants-block__prompt-msg');
      }
    }
    
    findVariant() {
      if (this.productForm) {
        return this.productForm.findVariant(this.variants, this.options, this.selectedOptions);
      }
      return this.variants.find(variant => {
        return this.options.every((option, index) => {
          const optionKey = 'option' + (index + 1);
          return variant[optionKey] === this.selectedOptions[option.name];
        });
      });
    }
    
    updateOptionAvailability() {
      this.options.forEach((option, optionIndex) => {
        const optionKey = 'option' + (optionIndex + 1);
        
        option.values.forEach(value => {
          // Check if ANY variant with this option value is available
          // considering the OTHER currently selected options
          const isAvailable = this.productForm
            ? this.productForm.isOptionValueAvailable(this.variants, this.options, this.selectedOptions, optionIndex, value)
            : this.variants.some(variant => {
            if (variant[optionKey] !== value) return false;
            if (!variant.available) return false;
            
            // Check if matches all OTHER selected options
            // (unselected/deferred options act as wildcards)
            return this.options.every((otherOption, otherIndex) => {
              if (otherIndex === optionIndex) return true;
              const otherValue = this.selectedOptions[otherOption.name];
              if (otherValue == null) return true;
              const otherKey = 'option' + (otherIndex + 1);
              return variant[otherKey] === otherValue;
            });
            });
          
          // Find and update button
          const btn = this.container.querySelector(
            `.product-variants-block__pill[data-option-name="${option.name}"][data-option-value="${value}"],
             .product-variants-block__swatch[data-option-name="${option.name}"][data-option-value="${value}"],
             .product-variants-block__rich-media-item[data-option-name="${option.name}"][data-option-value="${value}"]`
          );
          
          if (btn) {
            btn.disabled = !isAvailable;
          }
        });
      });
    }
    
    formatMoney(cents) {
      if (window.formatMoney) {
        return window.formatMoney(cents, this.moneyFormat);
      }
      if (window.formatMoneyFallback) {
        return window.formatMoneyFallback(cents);
      }
      return (Number(cents || 0) / 100).toFixed(2);
    }

    productHandle() {
      const pathname = window.location && window.location.pathname ? window.location.pathname : '';
      const match = pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    initFlashDeal() {
      const colors = window.HealixColors;
      const handle = this.productHandle();
      if (!handle || !colors || typeof colors.activeAutomaticDeal !== 'function') return;
      colors.activeAutomaticDeal(handle).then(match => {
        this.flashDeal = match && match.sale ? match.sale : null;
        if (this.currentVariant) this.updatePriceDisplays(this.currentVariant);
      }).catch(() => {
        this.flashDeal = null;
      });
    }

    ensureFlashPriceNodes(block, current, compare) {
      const bolt = '<svg data-flash-zap-icon viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Z"></path></svg>';
      let zap = block.querySelector('[data-flash-zap]');
      let flashRegular = block.querySelector('[data-flash-regular]');
      let flashList = block.querySelector('[data-flash-list]');

      if (!zap) {
        zap = document.createElement('span');
        zap.className = 'product-price-block__flash-zap';
        zap.setAttribute('data-flash-zap', '');
        block.insertBefore(zap, current || block.firstChild);
      }
      zap.innerHTML = bolt;

      if (!flashRegular) {
        flashRegular = document.createElement('span');
        flashRegular.className = 'product-price-block__flash-regular';
        flashRegular.setAttribute('data-flash-regular', '');
        block.insertBefore(flashRegular, compare || null);
      }
      if (!flashList) {
        flashList = document.createElement('span');
        flashList.className = 'product-price-block__flash-list';
        flashList.setAttribute('data-flash-list', '');
        block.insertBefore(flashList, compare || null);
      }

      return { zap, flashRegular, flashList };
    }

    updatePriceDisplays(variant) {
      const colors = window.HealixColors;
      const breakdown = colors && typeof colors.flashPriceBreakdown === 'function'
        ? colors.flashPriceBreakdown(variant, this.flashDeal)
        : null;

      this.container.querySelectorAll('.product-price-block').forEach(block => {
        const current = block.querySelector('[id^="product-price-current"]');
        const compare = block.querySelector('[id^="product-price-compare"]');
        const flashNodes = this.ensureFlashPriceNodes(block, current, compare);
        const flashRegular = flashNodes.flashRegular;
        const flashList = flashNodes.flashList;
        const zap = flashNodes.zap;

        if (breakdown && breakdown.active) {
          block.classList.toggle('product-price-block--flash-deal', true);
          if (current) {
            current.textContent = this.formatMoney(breakdown.flashCents);
            current.classList.toggle('product-price-block__current--on-sale', true);
          }
          if (compare) compare.style.display = 'none';
          if (zap) zap.hidden = false;
          if (flashRegular) {
            flashRegular.textContent = this.formatMoney(breakdown.sellingCents);
            flashRegular.hidden = false;
          }
          if (flashList) {
            flashList.textContent = breakdown.listCents
              ? 'List ' + this.formatMoney(breakdown.listCents)
              : '';
            flashList.hidden = !breakdown.listCents;
          }
          return;
        }

        block.classList.toggle('product-price-block--flash-deal', false);
        if (zap) zap.hidden = true;
        if (flashRegular) flashRegular.hidden = true;
        if (flashList) flashList.hidden = true;
        if (current) {
          current.textContent = this.formatMoney(variant.price);
          current.classList.toggle('product-price-block__current--on-sale',
            variant.compare_at_price && variant.compare_at_price > variant.price);
        }
        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.textContent = this.formatMoney(variant.compare_at_price);
            compare.style.display = '';
          } else {
            compare.style.display = 'none';
          }
        }
      });
    }
    
    updateVariant(variant) {
      const isAvailable = variant.available === true;
      this.currentVariant = variant;
      
      // Update hidden variant inputs
      this.container.querySelectorAll('input[name="id"], [data-variant-id]').forEach(input => {
        input.value = variant.id;
      });
      
      this.updatePriceDisplays(variant);
      
      // Update add to cart buttons
      this.container.querySelectorAll('.product-add-to-cart-block__btn').forEach(btn => {
        const btnText = btn.querySelector('[data-add-to-cart-text]');
        btn.disabled = !isAvailable;
        if (btnText) btnText.textContent = isAvailable ? 'Add to Cart' : 'Sold Out';
      });
      
      // Update buy now buttons
      this.container.querySelectorAll('.shopify-payment-button button').forEach(btn => {
        btn.disabled = !isAvailable;
      });
      this.container.querySelectorAll('.product-add-to-cart-block__dynamic-checkout').forEach(container => {
        container.style.display = isAvailable ? '' : 'none';
      });
      
      // Update URL
      if (!this.sectionId) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      
      // Update media gallery
      if (variant.featured_media) {
        document.dispatchEvent(new CustomEvent('product:media-change', {
          detail: { mediaId: variant.featured_media.id.toString(), sectionId: this.sectionId }
        }));
      }
      
      // Update stock block
      this.updateStockBlock(variant, isAvailable);
      
      // Dispatch event for other blocks. Also on document: the floating bar
      // listens there, and window-dispatched events don't reach document
      // listeners — without this its data-variant-id never followed the picker.
      const evtDetail = { detail: { variant: variant } };
      window.dispatchEvent(new CustomEvent('variant:changed', evtDetail));
      document.dispatchEvent(new CustomEvent('variant:changed', evtDetail));
    }
    
    updateStockBlock(variant, isAvailable) {
      this.container.querySelectorAll('.product-stock-block').forEach(stockBlock => {
        stockBlock.classList.remove('product-stock-block--in-stock', 'product-stock-block--low-stock', 'product-stock-block--out-of-stock');
        
        const message = stockBlock.querySelector('.product-stock-block__text');
        const barFill = stockBlock.querySelector('.product-stock-block__bar-fill');
        const threshold = parseInt(stockBlock.dataset.lowStockThreshold) || 5;
        const maxFillPercent = 80;
        let progressPercent = 0;
        
        if (!isAvailable) {
          stockBlock.classList.add('product-stock-block--out-of-stock');
          if (message) message.textContent = 'Out of Stock';
          progressPercent = 0;
        } else if (variant.inventory_management !== 'shopify') {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        } else if (variant.inventory_quantity <= 0) {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        } else if (variant.inventory_quantity <= threshold) {
          stockBlock.classList.add('product-stock-block--low-stock');
          if (message) message.textContent = `Only ${variant.inventory_quantity} left!`;
          progressPercent = Math.min((variant.inventory_quantity / threshold) * maxFillPercent, maxFillPercent);
        } else {
          stockBlock.classList.add('product-stock-block--in-stock');
          if (message) message.textContent = 'In Stock';
          progressPercent = maxFillPercent;
        }
        
        if (barFill) {
          barFill.style.setProperty('--progress', progressPercent + '%');
        }
      });
    }
  }

  /**
   * Product Media Gallery Class
   * Handles thumbnail navigation, swipe gestures, and media switching
   */
  class ProductMediaGallery {
    constructor(container) {
      this.container = container;
      this.id = container.id;
      this.layout = container.dataset.layout || 'collage';
      this.mobileLayout = container.dataset.mobileLayout || 'thumbnail';
      this.enableLightbox = container.dataset.enableLightbox === 'true';
      
      this.thumbnailsScrolls = [];
      this.desktopFeatured = container.querySelector('.product-media-gallery__featured');
      this.thumbnails = [];
      
      // Mobile carousel elements
      this.carouselSlider = container.querySelector('.product-media-gallery__carousel-slider');
      this.carouselItems = container.querySelectorAll('.product-media-gallery__carousel-item');
      
      this.currentIndex = 0;
      this.allMedia = [];
      
      this.refreshActiveThumbnails();
      // Build media data from thumbnails
      this.buildMediaData();
      
      this.init();
    }

    getActiveThumbnailScope() {
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;

      if (isDesktop && (this.layout === 'thumbnail' || this.layout === 'thumbnail_left')) {
        return this.container.querySelector('.product-media-gallery__desktop-thumbnails');
      }

      if (this.mobileLayout === 'thumbnail') {
        return this.container.querySelector('.product-media-gallery__mobile-thumbnails');
      }

      return null;
    }

    refreshActiveThumbnails() {
      const scope = this.getActiveThumbnailScope();
      this.thumbnailsScrolls = scope ? scope.querySelectorAll('.product-media-gallery__thumbnails-scroll') : [];
      this.thumbnails = scope ? scope.querySelectorAll('.product-media-gallery__thumbnail') : [];
    }

    // Centers the active thumbnail inside its own scroll rail only — never the page.
    scrollThumbnailIntoRail(thumb) {
      if (!thumb) return;
      const rail = thumb.closest('.product-media-gallery__thumbnails-scroll');
      if (!rail) return;

      const isVertical = this.layout === 'thumbnail_left' && window.matchMedia('(min-width: 990px)').matches;
      if (isVertical) {
        const target = thumb.offsetTop - (rail.clientHeight - thumb.clientHeight) / 2;
        rail.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      } else {
        const target = thumb.offsetLeft - (rail.clientWidth - thumb.clientWidth) / 2;
        rail.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }

    getSlideCount() {
      this.refreshActiveThumbnails();
      if (this.thumbnails.length && this.allMedia.length !== this.thumbnails.length) {
        this.buildMediaData();
      }

      return Math.max(this.thumbnails.length, this.carouselItems.length, this.allMedia.length);
    }
    
    buildMediaData() {
      this.allMedia = [];

      // Extract media info from each thumbnail
      this.thumbnails.forEach((thumb, fallbackIndex) => {
        const img = thumb.querySelector('.product-media-gallery__thumbnail-image');
        if (img) {
          const parsedIndex = parseInt(thumb.dataset.index, 10);
          const index = Number.isNaN(parsedIndex) ? fallbackIndex : parsedIndex;
          const type = thumb.dataset.mediaType || 'image';

          // Get high-res src from thumbnail src by replacing width
          const thumbSrc = img.src;
          const highResSrc = thumbSrc.replace(/width=\d+/, 'width=1200');
          const lightboxSrc = thumbSrc.replace(/width=\d+/, 'width=2400');
          
          this.allMedia[index] = {
            index: index,
            id: thumb.dataset.mediaId,
            type: type,
            src: highResSrc,
            srcset: `${thumbSrc.replace(/width=\d+/, 'width=600')} 600w, ${thumbSrc.replace(/width=\d+/, 'width=900')} 900w, ${highResSrc} 1200w`,
            lightboxSrc: lightboxSrc,
            alt: img.alt || ''
          };
        }
      });

      this.preloadFeaturedImages();
    }

    // Preload the featured-size (1200px) images so clicking a thumbnail swaps the
    // main image instantly instead of fetching on click. Deferred so it doesn't
    // compete with the initial render / first featured image.
    preloadFeaturedImages() {
      const run = () => {
        this._featuredPreload = this.allMedia
          .filter((media) => media && media.type === 'image')
          .map((m) => {
            const im = new Image();
            im.decoding = 'async';
            im.src = m.src;
            return im;
          });
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 1000 });
      } else {
        setTimeout(run, 200);
      }
    }
    
    init() {
      this.bindThumbnailClicks();
      this.bindMobileScrollSync();
      this.bindVariantMediaChange();

      if (this.enableLightbox) {
        this.bindLightbox();
      }
    }
    
    bindThumbnailClicks() {
      this.container.addEventListener('click', (e) => {
        const thumb = e.target.closest('.product-media-gallery__thumbnail');
        if (!thumb || !this.container.contains(thumb)) return;

        const activeScope = this.getActiveThumbnailScope();
        if (activeScope && !activeScope.contains(thumb)) return;

        const index = parseInt(thumb.dataset.index, 10);
        if (Number.isNaN(index)) return;
        this.goToSlide(index);
      });
    }
    
    bindMobileScrollSync() {
      if (!this.carouselSlider || !this.carouselItems.length) return;

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              const idx = parseInt(entry.target.dataset.index, 10);
              if (!Number.isNaN(idx)) this.syncFromScroll(idx);
            }
          });
        }, { root: this.carouselSlider, threshold: 0.6 });

        this.carouselItems.forEach((item) => io.observe(item));
        this.mobileScrollObserver = io;
      } else {
        let scrollTimer;
        this.carouselSlider.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const width = this.carouselSlider.clientWidth || 1;
            this.syncFromScroll(Math.round(this.carouselSlider.scrollLeft / width));
          }, 80);
        }, { passive: true });
      }
    }

    syncFromScroll(index) {
      this.currentIndex = index;

      this.refreshActiveThumbnails();
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });

      this.scrollThumbnailIntoRail(this.thumbnails[index]);
    }

    bindVariantMediaChange() {
      document.addEventListener('product:media-change', (e) => {
        const mediaId = e.detail.mediaId;
        const activeScope = this.getActiveThumbnailScope();
        let thumb = activeScope ? activeScope.querySelector(`[data-media-id="${mediaId}"]`) : null;
        if (!thumb) {
          thumb = this.container.querySelector(`[data-media-id="${mediaId}"]`);
        }
        
        if (thumb && thumb.dataset.index) {
          this.goToSlide(parseInt(thumb.dataset.index, 10));
        }
      });
    }
    
    bindLightbox() {
      this.container.querySelectorAll('.product-media-gallery__image').forEach(img => {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => {
          const lightboxSrc = img.dataset.lightboxSrc || img.src;
          this.openLightbox(lightboxSrc, img.alt);
        });
      });
    }
    
    goToSlide(index) {
      const maxIndex = this.getSlideCount();
      if (index < 0 || index >= maxIndex) return;

      this.pauseInlineVideos();
      this.currentIndex = index;
      
      // Update active thumbnail
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });
      
      // Scroll active thumbnail into view within its rail (never the page)
      this.scrollThumbnailIntoRail(this.thumbnails[index]);

      // Update mobile carousel
      this.updateMobileCarousel(index);
      
      // Update desktop featured image
      this.updateDesktopFeaturedImage(index);

      // Video thumbnails behave like media selectors: play the selected video
      // in the gallery instead of launching the fullscreen viewer.
      this.playSelectedInlineVideo(index);
    }

    pauseInlineVideos(exceptVideo = null) {
      this.container.querySelectorAll('video').forEach((video) => {
        if (video !== exceptVideo && !video.paused) video.pause();
      });

      this.container.querySelectorAll('iframe[data-inline-video-playing="true"]').forEach((iframe) => {
        iframe.removeAttribute('data-inline-video-playing');
        iframe.src = this.setExternalVideoAutoplay(iframe.src, false);
      });
    }

    setExternalVideoAutoplay(src, shouldPlay) {
      if (!src) return src;

      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('autoplay', shouldPlay ? '1' : '0');
        if (shouldPlay) {
          url.searchParams.set('playsinline', '1');
          url.searchParams.set('mute', '1');
          url.searchParams.set('muted', '1');
        }
        return url.toString();
      } catch (error) {
        return src;
      }
    }

    playSelectedInlineVideo(index) {
      const media = this.allMedia[index];
      if (!media || (media.type !== 'video' && media.type !== 'external_video')) return;

      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const selectedItem = isDesktop ? this.desktopFeatured : this.carouselItems[index];
      if (!selectedItem) return;

      const video = selectedItem.querySelector('video');
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.controls = true;
        this.pauseInlineVideos(video);
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        return;
      }

      const iframe = selectedItem.querySelector('iframe');
      if (iframe) {
        this.pauseInlineVideos();
        iframe.src = this.setExternalVideoAutoplay(iframe.src, true);
        iframe.setAttribute('data-inline-video-playing', 'true');
      }
    }
    
    updateMobileCarousel(index) {
      if (!this.carouselSlider || !this.carouselItems.length) return;
      if (!this.carouselItems[index]) return;

      const left = index * this.carouselSlider.clientWidth;
      if (Math.abs(this.carouselSlider.scrollLeft - left) < 2) return;

      this.carouselSlider.scrollTo({ left: left, behavior: 'smooth' });
    }
    
    updateDesktopFeaturedImage(index) {
      if (!this.desktopFeatured || !this.allMedia[index]) return;
      
      const media = this.allMedia[index];
      let image = this.desktopFeatured.querySelector('.product-media-gallery__featured-image');

      if (media.type !== 'image') {
        const sourceItem = this.carouselItems[index];
        const sourceWrapper = sourceItem && sourceItem.querySelector(
          '.product-media-gallery__video-wrapper, .product-media-gallery__model-wrapper'
        );
        if (!sourceWrapper) return;

        this.desktopFeatured.replaceChildren(sourceWrapper.cloneNode(true));
        this.desktopFeatured.dataset.mediaId = media.id;
        this.desktopFeatured.dataset.mediaType = media.type;
        this.desktopFeatured.dataset.index = index;
        if (typeof this.container.refreshMediaViewer === 'function') this.container.refreshMediaViewer();
        return;
      }

      if (!image) {
        const sourceItem = this.carouselItems[index];
        const sourceWrapper = sourceItem && sourceItem.querySelector('.product-media-gallery__image-wrapper');
        if (!sourceWrapper) return;

        const replacement = sourceWrapper.cloneNode(true);
        image = replacement.querySelector('img');
        if (image) image.classList.add('product-media-gallery__featured-image');
        this.desktopFeatured.replaceChildren(replacement);
      }
      
      if (image && media.type === 'image') {
        // Animate transition with GSAP if available
        if (typeof gsap !== 'undefined') {
          gsap.to(image, {
            opacity: 0,
            scale: 0.98,
            duration: 0.15,
            ease: 'power2.in',
            onComplete: () => {
              // Update image source
              image.src = media.src;
              image.srcset = media.srcset;
              image.alt = media.alt;
              image.dataset.lightboxSrc = media.lightboxSrc;
              
              // Update container data attributes
              this.desktopFeatured.dataset.mediaId = media.id;
              this.desktopFeatured.dataset.mediaType = media.type;
              this.desktopFeatured.dataset.index = index;
              
              // Animate in
              gsap.to(image, {
                opacity: 1,
                scale: 1,
                duration: 0.2,
                ease: 'power2.out'
              });
            }
          });
        } else {
          // Fallback without animation
          image.src = media.src;
          image.srcset = media.srcset;
          image.alt = media.alt;
          image.dataset.lightboxSrc = media.lightboxSrc;
          this.desktopFeatured.dataset.mediaId = media.id;
          this.desktopFeatured.dataset.mediaType = media.type;
          this.desktopFeatured.dataset.index = index;
        }
      }
    }

    // Additive public API — used ONLY by set-mode gallery swap (healix-set-gallery.js).
    // Re-scrapes thumbnails after the DOM's media items are replaced and resets to
    // slide 0. No-op impact on normal PDPs: nothing calls this unless set mode does.
    refresh() {
      this.currentIndex = 0;
      this.carouselItems = this.container.querySelectorAll('.product-media-gallery__carousel-item');
      this.refreshActiveThumbnails();
      this.buildMediaData();
      if (this.thumbnails.length) {
        this.thumbnails.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === 0);
        });
      }
      this.updateDesktopFeaturedImage(0);
      this.updateMobileCarousel(0);
    }

    openLightbox(src, alt) {
      // Create lightbox overlay
      const overlay = document.createElement('div');
      overlay.className = 'product-lightbox';
      overlay.innerHTML = `
        <div class="product-lightbox__backdrop"></div>
        <div class="product-lightbox__content">
          <img src="${src}" alt="${alt || ''}" class="product-lightbox__image">
          <button type="button" class="product-lightbox__close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      `;
      
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      
      // Animate in with GSAP if available
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.3 });
        gsap.fromTo(overlay.querySelector('.product-lightbox__image'), 
          { scale: 0.9, opacity: 0 }, 
          { scale: 1, opacity: 1, duration: 0.3, delay: 0.1 }
        );
      }
      
      // Close handlers
      const close = () => {
        if (typeof gsap !== 'undefined') {
          gsap.to(overlay, { 
            opacity: 0, 
            duration: 0.2, 
            onComplete: () => {
              overlay.remove();
              document.body.style.overflow = '';
            }
          });
        } else {
          overlay.remove();
          document.body.style.overflow = '';
        }
      };
      
      overlay.querySelector('.product-lightbox__close').addEventListener('click', close);
      overlay.querySelector('.product-lightbox__backdrop').addEventListener('click', close);
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    }
  }

  /**
   * PDP colourway picker — hydrated from the Healix colors backend.
   * Each colourway is its own product, so this is NOT a Shopify option:
   * the strip renders the family's colours split into Core / Limited from
   * HealixColors.siblingsRaw(handle) (per-product siblings endpoint —
   * coalesced batch + localStorage cache shared with every other surface).
   * Swatch construction/classes are the collection family-header ones
   * (.cfs-swatch), so PDP and collection swatches look identical. The active
   * swatch is THIS product's colour; clicking another navigates to that
   * colourway's product URL.
   */
  function initPdpColorways() {
    const host = document.querySelector('[data-pdp-colorways]');
    if (!host || !window.HealixColors || !window.HealixColors.siblingsRaw) return;
    
    const handle = host.getAttribute('data-product-handle') || '';
    const ownSlug = (host.getAttribute('data-color-slug') || '').toLowerCase();
    const mount = host.querySelector('[data-colorways-host]');
    const nameEl = host.querySelector('[data-colorways-name]');
    if (!handle || !mount) return;

    // Perceived luminance (WCAG-ish) → white vs dark chip text on the hex.
    // Mirrors tagTextColor in assets/collection-family-sections.js so the PDP
    // colour chip matches the product-card colour chip exactly.
    function tagTextColor(hex) {
      let h = String(hex || '').replace('#', '');
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      if (h.length !== 6) return '#FFFFFF';
      const r = parseInt(h.slice(0, 2), 16) / 255;
      const g = parseInt(h.slice(2, 4), 16) / 255;
      const b = parseInt(h.slice(4, 6), 16) / 255;
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return lum > 0.6 ? '#1D2129' : '#FFFFFF';
    }

    // Render the selected colour as a hex CHIP into [data-colorways-name], reusing
    // the theme tag component's classes/inline-style exactly as makeTag builds the
    // product-card colour chip (assets/collection-family-sections.js): white/dark
    // text picked by luminance, --tag-hex driving the solid background.
    function renderColorChip(name, hex, slug) {
      if (!nameEl) return;
      nameEl.textContent = '';
      const tag = document.createElement('span');
      tag.className = 'tag tag--mini tag--solid product-card__badge' + (hex ? ' tag--hex' : ' tag--default');
      if (hex) {
        tag.style.setProperty('--tag-hex', hex);
        tag.style.setProperty('--tag-hex-text', tagTextColor(hex));
      }
      if (slug) tag.setAttribute('data-color-slug', slug);
      const txt = document.createElement('span');
      txt.className = 'tag__text';
      txt.textContent = name;
      tag.appendChild(txt);
      nameEl.appendChild(tag);
    }

    function makeSwatch(color, editionLabel) {
      const isActive = String(color.color || '').toLowerCase() === ownSlug;
      const name = color.colorName || color.color || 'Colour';
      const el = document.createElement(isActive ? 'span' : 'a');
      el.className = 'cfs-swatch' + (isActive ? ' is-active' : '');
      if (!isActive) el.href = color.url || ('/products/' + color.handle);
      el.setAttribute('aria-label', name + ' — ' + editionLabel);
      el.title = name;
      if (color.color) el.setAttribute('data-color-slug', color.color);
      if (isActive) el.setAttribute('aria-current', 'true');
      
      const chip = document.createElement('span');
      chip.className = 'cfs-swatch__chip';
      if (color.hex) chip.style.backgroundColor = color.hex;
      el.appendChild(chip);
      
      if (isActive) renderColorChip(name, color.hex, color.color);
      return el;
    }
    
    window.HealixColors.siblingsRaw(handle).then(function (sib) {
      if (!sib) return; // API failed — strip stays hidden, no dead UI
      const groups = [
        { label: 'Core', items: sib.core || [] },
        { label: 'Limited', items: sib.limited || [] }
      ];
      let any = false;
      mount.textContent = '';
      groups.forEach(function (g) {
        if (!g.items.length) return;
        any = true;
        const groupEl = document.createElement('div');
        groupEl.className = 'cfs-swatches__group';
        const label = document.createElement('span');
        label.className = 'cfs-swatches__label';
        label.textContent = g.label;
        groupEl.appendChild(label);
        const dots = document.createElement('div');
        dots.className = 'cfs-swatches__dots';
        g.items.forEach(function (c) { dots.appendChild(makeSwatch(c, g.label)); });
        groupEl.appendChild(dots);
        mount.appendChild(groupEl);
      });
      if (any) host.hidden = false;
    });
  }

  /**
   * Initialize galleries on DOM ready
   */
  function init() {
    document.querySelectorAll('.product-media-gallery').forEach(gallery => {
      const instance = new ProductMediaGallery(gallery);
      // Additive: expose the instance on its container so the set-mode swap
      // module (healix-set-gallery.js) can call instance.refresh() after it
      // replaces component media. Purely a reference — no behavior change.
      gallery.productMediaGalleryInstance = instance;
    });
    initPdpColorways();
  }
  
  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize on Shopify section refresh
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const gallery = event.target.querySelector('.product-media-gallery');
      if (gallery) {
        new ProductMediaGallery(gallery);
      }
    });
  }
  
  // Expose for external use
  window.ProductMediaGallery = ProductMediaGallery;
  window.ProductVariantSelector = ProductVariantSelector;
})();

/**
 * Add lightbox styles dynamically
 */
(function() {
  const style = document.createElement('style');
  style.textContent = `
    .product-lightbox {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .product-lightbox__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.9);
    }
    
    .product-lightbox__content {
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
    }
    
    .product-lightbox__image {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
    }
    
    .product-lightbox__close {
      position: absolute;
      top: -40px;
      right: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    
    .product-lightbox__close:hover {
      opacity: 0.7;
    }
  `;
  document.head.appendChild(style);

})();
